import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { dashboardQuery, type TopDevice, type TopUser, type WorkingTime, type WorkingTimeUser } from '../api/productivitee'
import { BoardGrid, BoardLook, BoardPart, BoardStat, BoardStrip } from '../dashboard/BoardLook'
import { OdbCard } from '../dashboard/OdbCard'
import { ChartEmpty, VerticalBarChart } from '../inventoree/EndpointCharts'
import { durationString } from '../easeedesk/format'
import { WORK_COLORS, WorkTimeChart, type WorkTimeRow } from './DashboardCharts'

/**
 * Tabloul Productivitee - `productivitee/dashboard`, adica
 * `views/version-3/productivitee/dashboard/{index,dashboard}.php` si
 * `ProductiviteeDashboard` din `public/js/productivitee.js`.
 *
 * -----------------------------------------------------------------------------
 * CE ERA GRESIT, SI CE SE SCHIMBA
 * -----------------------------------------------------------------------------
 *
 * Doua lucruri, amandoua vazute de om pe `optimumdesk.ai`:
 *
 *   1. LIPSEAU GRAFICELE. Ecranul avea patru cifre si trei desene: "Device
 *      Working Time (first 15 devices)", "Top Productive Devices" si "Top
 *      Unproductive Devices". Desenele erau amCharts, biblioteca a iesit din
 *      portal sub auditul bancar din septembrie 2026, si odata cu ea au plecat
 *      si cele trei capete care le hraneau. In v4 ramasesera doar cele patru
 *      cifre. Se intorc, desenate de mana in SVG (`DashboardCharts.tsx` si
 *      `VerticalBarChart`), din citirile care n-au fost niciodata rupte -
 *      `Productivity::workingTimeDevices` si `productiveTopDevices`;
 *      (21 septembrie 2026, Roxana: "mai fa 3 grafice pe user". Aceleasi trei,
 *      pe oameni, din tabelele listei User Productivity - vezi mai jos de ce
 *      sunt DOUA seturi si nu unul cu comutator);
 *   2. NU ERA PE DESENUL NOU. Patru dale Metronic (`dashboard-stat yellow`) si
 *      trei `panel panel-warning`, adica ultimul ecran din portal care mai
 *      vorbea limba de dinainte de tablouri. Acum e o BANDA de contoare si o
 *      GRILA de carduri, pe `od_dashboard.css` - aceeasi foaie, aceeasi cutie,
 *      aceeasi tipografie ca tabloul Protectee si ca celelalte.
 *
 * -----------------------------------------------------------------------------
 * DE CE NU E `ModuleBoardPage`, CA LA PROTECTEE
 * -----------------------------------------------------------------------------
 *
 * Tabloul de modul (Protectee, Softsee, Opteemum...) isi ia cardurile dintr-un
 * CATALOG scris de mana - `application/Services/Dashboard/Catalog.php` - si
 * de acolo ii vin si perspectivele (Technician / IT Manager), clasa de active
 * si fereastra de timp. Fisierul ala e COMUN cu portalul care ruleaza la banca,
 * si Productivitee nu are niciun rand in el: nici modulul, nici vreun card.
 *
 * Deci bara cu Technician / IT Manager NU se poate pune de aici, si nu se pune
 * pe furis: ar fi insemnat o schimbare in stratul comun cu productia CEC. Se
 * ia DESENUL si se lasa masinaria, exact ca la Endpoint Performance, SNMP si
 * Core Applications - ecranele de MASURATORI, care au grafice desenate in
 * React din raspunsul lor propriu si n-au ce cauta intr-un catalog de cifre cu
 * praguri si istorie pe zile.
 *
 * -----------------------------------------------------------------------------
 * DOUA PARTI, NU UN COMUTATOR INTRE MASINI SI OAMENI
 * -----------------------------------------------------------------------------
 *
 * Cele sase desene raspund la aceeasi intrebare pentru doua lucruri diferite,
 * si tentatia e un comutator "Devices | Users" deasupra a trei carduri. Nu:
 * cele doua parti se citesc din tabele care n-au NIMIC in comun - masinile din
 * `it_productivitee_device_summary` prin `it_echipamente`, oamenii din
 * `it_logged_users_list` cu `it_productivitee_user_summary`. Intr-o zi in care
 * una e goala cealalta poate fi plina, si asta chiar s-a intamplat: pe
 * optimumdesk.ai lista oamenilor era plina si cea a masinilor goala, fiindca
 * masinile mai trec prin sapte porti.
 *
 * Un comutator ar fi ascuns jumatate din raspuns in spatele unei apasari, si
 * omul care se uita la partea goala n-ar fi avut de unde sti ca cealalta are
 * date. Asezate una sub alta, cu numele lor deasupra, se citesc amandoua
 * dintr-o privire - si pe un ecran ingust grila se pune oricum pe o coloana.
 *
 * DESENUL e insa acelasi, si se scrie o singura data: `ChartTrio` mai jos.
 * Oamenii stau PRIMII - modulul se uita la ziua omului, masina e unealta.
 *
 * -----------------------------------------------------------------------------
 * CELE DOUA CAPETE NU SE MAI SUPRAPUN
 * -----------------------------------------------------------------------------
 *
 * Roxana, 21 septembrie 2026: "la top productive user si top unproductive user
 * apare acelasi user cu 100%".
 *
 * Asa era, si nu numai la oameni. Cele doua desene erau ACEEASI citire cu
 * ordinea intoarsa si `LIMIT 10` la amandoua - asa le scrie si portalul vechi,
 * de cand a fost scris. Intr-o firma cu zece masini sau zece oameni, amandoua
 * desenele arata pe toata lumea, doar in alta ordine, iar cel mai productiv om
 * al zilei aparea in fruntea celor neproductivi.
 *
 * Acum e o LINIE, nu o directie: de la 50% in sus intr-o lista, sub 50% in
 * cealalta (`ProductiviteeSource::HALF_PERCENT`). Cele doua sunt despartite
 * prin constructie, iar cand nimeni nu e sub linie desenul de jos spune asta cu
 * vorbele lui - "toata lumea a fost activa macar jumatate din ziua de lucru" -
 * in loc sa imprumute de la celalalt capat.
 *
 * -----------------------------------------------------------------------------
 * CE NU SE REPARA AICI, CU BUNA STIINTA
 * -----------------------------------------------------------------------------
 *
 * "Average Productivity" scrie 0.00% si pe portalul vechi, si aici: functia
 * din baza e chemata cu o data acolo unde asteapta un numar. E o hotarare
 * separata a Roxanei, si ecranul asta NU se sprijina pe ea - cifra se arata
 * cum vine, ca inainte, si nici un card nu-si schimba starea din cauza ei.
 */

/** Randul mic si majuscul de deasupra titlului - `<div class="more">Applications</div>` al dalei vechi. */
const KICKER = 'Applications'

/**
 * Cardul fara ce desena spune asta LINISTIT, cu vorbele cardurilor din catalog:
 * "No data yet", si dedesubt de ce - niciodata un desen gol cu axe si nimic in
 * el, care arata a ecran stricat.
 */
function NoData({ children }: { children: ReactNode }) {
  return (
    <ChartEmpty>
      <span>
        <span className="odb-unknown-why">No data yet</span>
        <br />
        {children}
      </span>
    </ChartEmpty>
  )
}

export function ProductiviteeDashboardPage() {
  const q = useQuery(dashboardQuery())
  const d = q.data

  /* Ora medie de pornire lipseste cand nicio masina n-a scris sumarul de azi -
     acolo ecranul vechi scria "n/a"; aici e "—" si "No data yet", ca pe carduri. */
  const noStart = !d || d.start_time === null

  if (q.error) {
    return (
      <BoardLook>
        <div className="odb-empty">
          <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> Something went wrong: {q.error.message}
        </div>
      </BoardLook>
    )
  }

  return (
    <BoardLook>
      <BoardStrip>
        <BoardStat
          module="productivitee"
          icon="fa-chart-line"
          value={d ? d.productivity : '—'}
          unit={d ? '%' : undefined}
          kicker={KICKER}
          title="Average Productivity"
        />
        <BoardStat
          module="productivitee"
          icon="fa-hourglass-half"
          value={d ? durationString(d.productive_time) : '—'}
          kicker={KICKER}
          title="Average Productivity Time"
        />
        <BoardStat
          module="productivitee"
          tone={noStart ? 'unknown' : 'flat'}
          icon="fa-clock"
          value={noStart ? '—' : d.start_time}
          kicker={noStart ? 'No data yet' : KICKER}
          title="Average Start Time"
        />
        <BoardStat
          module="productivitee"
          icon="fa-minus-circle"
          value={d ? durationString(d.inactive_time) : '—'}
          kicker={KICKER}
          title="Average Inactive Time"
        />
      </BoardStrip>

      {/* OAMENII INTAI, masinile dupa (Roxana, 21 septembrie 2026): modulul se
          uita la cum isi petrece omul ziua, iar masina e unealta cu care o
          petrece. Ecranul incepe cu raspunsul pentru care a fost deschis. */}
      <BoardPart title="Users">
        <ChartTrio
          ids={['userWorkingTime', 'productiveUsers', 'unproductiveUsers']}
          titles={['User Working Time (first 15 users)', 'Top Productive Users', 'Top Unproductive Users']}
          empty={[
            'Nobody wrote a working day today, so there is nothing to draw yet.',
            'Nobody was active for at least half of their working time today.',
            'Everybody who worked today was active for at least half of their working time. Nobody belongs here.',
          ]}
          working={(d?.user_working_time ?? []).map(userRow)}
          best={(d?.user_top_productive ?? []).map(userTop)}
          worst={(d?.user_top_unproductive ?? []).map(userTop)}
        />
      </BoardPart>

      <BoardPart title="Devices">
        <ChartTrio
          grid="odbGridDevices"
          ids={['deviceWorkingTime', 'productiveDevices', 'unproductiveDevices']}
          titles={['Device Working Time (first 15 devices)', 'Top Productive Devices', 'Top Unproductive Devices']}
          empty={[
            'No machine wrote a working day today, so there is nothing to draw yet.',
            'No machine was productive for at least half of its working day today.',
            'Every machine that worked today was productive for at least half of its day. Nothing belongs here.',
          ]}
          working={(d?.working_time ?? []).map(deviceRow)}
          best={(d?.top_productive ?? []).map(deviceTop)}
          worst={(d?.top_unproductive ?? []).map(deviceTop)}
        />
      </BoardPart>
    </BoardLook>
  )
}

/**
 * Omul fara nume nu lasa eticheta goala sub coloana lui.
 *
 * `odDeviceName` din `od_ux.js` scrie "no name (#12)" pentru o masina fara
 * nume, si `columns.ts` face la fel in lista. Sub un grafic n-avem cum sa
 * inclinam cerneala - textul din SVG e text gol - dar vorbele raman aceleasi:
 * o coloana fara eticheta e o coloana despre care nimeni nu poate spune nimic.
 */
export function personLabel(row: { id: number; name: string }): string {
  const name = row.name.trim()
  return name !== '' ? name : `no name (#${row.id})`
}

const deviceRow = (r: WorkingTime): WorkTimeRow => ({ name: r.name, active_time: r.active_time, inactive_time: r.inactive_time })
const userRow = (r: WorkingTimeUser): WorkTimeRow => ({ name: personLabel(r), active_time: r.active_time, inactive_time: r.inactive_time })
const deviceTop = (r: TopDevice) => ({ name: r.name, value: r.value })
const userTop = (r: TopUser) => ({ name: personLabel(r), value: r.value })

/**
 * Cele trei desene ale unei parti - scrise O SINGURA DATA.
 *
 * Masinile si oamenii raspund la aceeasi intrebare pe doua drumuri care n-au
 * niciun tabel comun, dar DESENUL e acelasi: timpul de lucru al primilor
 * cincisprezece, cei mai productivi zece, cei mai putin productivi zece. Doua
 * copii ale acestor trei carduri ar fi insemnat doua locuri in care sa uiti
 * aceeasi indreptare - si sase carduri care se departeaza unul de altul la
 * prima schimbare, exact cum s-au departat cele 58 de `dashboard-stat`.
 *
 * Ce difera intra ca parametri: numerele cardurilor (cele vechi ale portalului
 * pentru masini), titlurile, si propozitia care se scrie cand n-are ce desena.
 */
function ChartTrio({
  grid,
  ids,
  titles,
  empty,
  working,
  best,
  worst,
}: {
  /** Numarul grilei - doua grile pe acelasi ecran nu pot avea acelasi. */
  grid?: string
  /** Numerele celor trei carduri: timp de lucru, top, coada. */
  ids: [string, string, string]
  titles: [string, string, string]
  /**
   * De ce e gol, cate o propozitie pentru fiecare card.
   *
   * Cele doua topuri au propozitii DIFERITE, si asta e miezul indreptarii din
   * 21 septembrie 2026: capatul de jos gol nu inseamna "n-avem date", inseamna
   * "toata lumea a fost peste linie astazi" - o veste buna, si se scrie ca
   * atare. Pana atunci capatul de jos nu era niciodata gol, fiindca imprumuta
   * de la celalalt.
   */
  empty: [string, string, string]
  working: WorkTimeRow[]
  best: { name: string; value: number }[]
  worst: { name: string; value: number }[]
}) {
  return (
    <BoardGrid id={grid}>
      {/* Doua coloane si doua randuri, ca la vecini: intr-un singur rand de
          grila (cel putin 200px) nu incape un desen cu axa in durate si cu
          cincisprezece etichete sub el. */}
      <OdbCard module="productivitee" icon="fa-chart-area" title={titles[0]} w={2} h={2} id={ids[0]}>
        {(height) =>
          working.length === 0 ? (
            <NoData>{empty[0]}</NoData>
          ) : (
            <>
              <WorkTimeChart rows={working} height={height - 22} />
              <Legend />
            </>
          )
        }
      </OdbCard>

      <OdbCard module="productivitee" icon="fa-thumbs-up" title={titles[1]} w={1} h={2} id={ids[1]}>
        {(height) =>
          best.length === 0 ? (
            <NoData>{empty[1]}</NoData>
          ) : (
            <VerticalBarChart
              height={height}
              items={best.map((r) => ({ label: r.name, value: r.value, color: '#85C5E3', tip: `${r.value}%` }))}
            />
          )
        }
      </OdbCard>

      <OdbCard module="productivitee" icon="fa-thumbs-down" title={titles[2]} w={1} h={2} id={ids[2]}>
        {(height) =>
          worst.length === 0 ? (
            <NoData>{empty[2]}</NoData>
          ) : (
            <VerticalBarChart
              height={height}
              items={worst.map((r) => ({ label: r.name, value: r.value, color: WORK_COLORS.inactive, tip: `${r.value}%` }))}
            />
          )
        }
      </OdbCard>
    </BoardGrid>
  )
}

/**
 * Cele doua serii ale graficului, scrise.
 *
 * Graficul vechi le spunea numai in balonul de sub cursor ("Active time",
 * "Inactive time"), deci pe un ecran de perete, la care nimeni nu tine mausul,
 * albastrul si rosul nu insemnau nimic. Culoarea nu e singurul semn: fiecare
 * are si numele langa ea.
 */
function Legend() {
  return (
    <div style={{ display: 'flex', gap: 16, justifyContent: 'center', fontSize: 11, color: '#666', height: 22, alignItems: 'center' }}>
      <span>
        <span style={{ display: 'inline-block', width: 10, height: 10, background: WORK_COLORS.active, opacity: 0.6, marginRight: 5 }} /> Active time
      </span>
      <span>
        <span style={{ display: 'inline-block', width: 10, height: 2, background: WORK_COLORS.inactive, marginRight: 5, verticalAlign: 'middle' }} /> Inactive time
      </span>
    </div>
  )
}
